const article02 = `# What Is a One Rep Max, and Why Do People Estimate It?
**2025-09-01**

In strength training, the term **"One Rep Max" (1RM)** refers to the maximum amount of weight someone can lift for exactly **one** repetition of a given exercise — usually a squat, deadlift, or bench press.

It’s a foundational concept in both sports performance and general strength programming. But what’s interesting is that **most people never actually test it.**

They estimate it instead. Here's why.

---

## 🧮 Why Not Just Lift the Max?

Testing a 1RM directly means pushing your body to the absolute limit — which can be:
- Physically risky without proper supervision
- Technically demanding (requires perfect form)
- Not always practical during regular training blocks

Instead, most lifters perform **submaximal sets** (e.g. 5 reps at a challenging weight), then use mathematical formulas to **estimate** their 1RM.

---

## 📐 Common Estimation Formulas

There are several formulas used to predict 1RM based on reps and weight. A few of the most well-known:

- **Epley Formula**:  
  '1RM = Weight × (1 + Reps ÷ 30)'

- **Brzycki Formula**:  
  '1RM = Weight × 36 ÷ (37 - Reps)'

- **Lombardi Formula**:  
  '1RM = Weight × Reps^0.10'

Each formula makes different assumptions about fatigue and strength curves, but they usually land within a similar range — especially for sets of 3–10 reps.

---

## 📊 Why Estimation Still Matters

Estimating your 1RM can help lifters and coaches:
- Track strength progress over time  
- Prescribe training loads using percentages (e.g. 75% of 1RM)  
- Set realistic goals without maxing out

It’s not about absolute accuracy — it’s about **having a consistent reference point** for smarter programming.

---

## ⚙️ Where This Shows Up in Training Tech

You’ll often find 1RM calculators inside training apps and lifting programs. These calculators:
- Ask for a recent set (weight x reps)
- Apply a formula
- Return your estimated 1RM and suggested multi-rep targets (e.g. 85% for 5 reps)

They’re a small piece of a larger system — but they offer a glimpse into how **basic math + observation** can make training more efficient.

---

*This article is for informational purposes only. Always consult a qualified coach or trainer for personalized advice.*
`;
export default article02;